import { Input, Radio, Tooltip } from "antd";
import { Flex } from "@chakra-ui/layout";
import type { TableColumnType } from "antd";
import { MdDelete, MdWarning } from "react-icons/md";
import InsumoSelect from "./InsumoSelect";
import AlvoSelect from "./AlvoSelect";
import ComponenteSelect from "./ComponenteSelect";
import OperacaoTag from "./OperacaoTag";
import { Dispatch, SetStateAction } from "react";
import type { InsumoSelectPayload } from "../consultarEstoque/types";

const { Group } = Radio;

function getColumnsLocais(
  handleChangeLocal: (talhaoId: string, key: string, value: string) => void,
): TableColumnType<any>[] {
  return [
    {
      title: "Talhão",
      dataIndex: "codigo",
      key: "codigo",
      fixed: "left",
      width: 60,
      align: "center",
      render: (value: string) => <span>{value ?? "-"}</span>,
    },
    {
      title: "Classe de Operação Selecionada",
      dataIndex: "planoOperacionalOperacaoTalhao",
      key: "planoOperacionalOperacaoTalhao",
      fixed: "left",
      width: 200,
      align: "center",
      render: (value: any[]) => <OperacaoTag value={value} />,
    },
    {
      title: "Área Total (ha)",
      dataIndex: "planoOperacionalOperacaoTalhao",
      key: "planoOperacionalOperacaoTalhao",
      fixed: "left",
      width: 60,
      align: "center",
      render: (dataValue: any) => {
        const area = dataValue
          ?.find((operacao: any) => operacao.areaDisponivelHa > 0)
          ?.areaDisponivelHa;
      return <span>{area ? area.toFixed(2) : 0}</span>
    },
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      fixed: "left",
      width: 60,
      align: "center",
      render: (value: string, record: any) => (
        <Group
          size="large"
          value={value}
          onChange={e => handleChangeLocal(record.talhaoId, "tipo", e.target.value)}
        >
          <Radio value="porcentagem">%</Radio>
          <Radio value="area">ha</Radio>
        </Group>
      ),
    },
    {
      title: "Valor a Executar",
      dataIndex: "valorExecutar",
      key: "valorExecutar",
      fixed: "left",
      width: 60,
      align: "center",
      render: (value: string, record: any) => (
        <Flex align="center" gridGap={2}>
          <Input
            size="large"
            placeholder="Valor"
            value={value}
            onChange={(e) => {
              const value = e.target.value;

              if (!/^\d*[.,]?\d*$/.test(value)) return;

              const normalized = value.replace(",", ".");

              handleChangeLocal(record.talhaoId, "valorExecutar", normalized);
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (!value) return;

              const normalized = value.replace(",", ".");
              const parsed = parseFloat(normalized);

              if (!isNaN(parsed)) {
                const max = record.tipo === "porcentagem" ? 100 : record.areaPlantadaHa;
                const limited = Math.min(parsed, max);
                handleChangeLocal(record.talhaoId, "valorExecutar", limited.toString());
              }
            }}
            style={{ borderRadius: 10 }}
          />
          <span style={{ fontSize: 16 }}>{record.tipo === "porcentagem" ? "%" : "ha"}</span>
        </Flex>
      ),
    },
    {
      title: "Área Efetiva (ha)",
      dataIndex: "areaEfetiva",
      key: "areaEfetiva",
      fixed: "left",
      width: 60,
      align: "center",
      render: (value: number) => <p style={{ fontWeight: "bold", color: "#4caf50" }}>{value ? Number(value).toFixed(2) : 0}</p>,
    },
  ];
}

function getColumnsRecursos(
  recursos: any[],
  unidadeIdExterno: string,
  handleDeleteRecurso: (index: number) => void,
  handleChangeRecurso: (id: number, key: string, value: string | string[] | InsumoSelectPayload) => void,
  setSearchComponent: Dispatch<SetStateAction<Record<number, string>>>,
  searchComponent: Record<number, string>,
  refDose: React.RefObject<any>,
  ocupacaoId: string,
  alertaByRecursoId: Record<number, boolean> = {},
): TableColumnType<any>[] {
  return [
    {
      title: "Componente",
      dataIndex: "componenteIdExterno",
      key: "componenteIdExterno",
      fixed: "left",
      align: "center",
      render: (value: string, record: any, index: number) => (
        <ComponenteSelect
          id={value}
          recursos={recursos}
          onChange={(idExterno) => handleChangeRecurso(record.id, "componenteIdExterno", idExterno)}
          unidadeIdExterno={unidadeIdExterno}
          searchComponent={searchComponent}
          index={index}
          onSearch={(searchText) => {
            setSearchComponent((prev) => ({
              ...prev,
              [index]: searchText,
            }));
          }}
        />
      ),
    },
    {
      title: "Insumo",
      dataIndex: "insumoIdExterno",
      key: "insumoIdExterno",
      fixed: "left",
      align: "center",
      render: (value: string, record: any) => (
        <InsumoSelect
          id={record.id}
          insumoId={value}
          idExternoUnidade={unidadeIdExterno}
          idExternoComponente={record?.componenteIdExterno}
          onChange={(payload) => {
            handleChangeRecurso(record.id, "insumo", payload);
            refDose.current?.focus();
          }}
        />
      ),
    },
    {
      title: "Dose",
      dataIndex: "quantidade",
      key: "quantidade",
      fixed: "left",
      width: 120,
      align: "center",
      render: (value: number, record: any) => (
        <Input
          type="number"
          size="large"
          placeholder="Dose"
          ref={refDose}
          value={value}
          onChange={e => handleChangeRecurso(record.id, "quantidade", e.target.value)}
          style={{ borderRadius: 10, textAlign: "center" }}
        />
      ),
    },
    {
      title: "Unidade",
      dataIndex: "unidade",
      key: "unidade",
      fixed: "left",
      width: 80,
      align: "center",
      render: (value: string) => <span>{value ?? "-"}</span>,
    },
    {
      title: "% Utilização",
      dataIndex: "porcentagemUtilizacao",
      key: "porcentagemUtilizacao",
      fixed: "left",
      width: 140,
      align: "center",
      render: (value: number, record: any) => (
        <Flex align="center" gridGap={2}>
          <Input
            type="number"
            size="large"
            placeholder="Utilização"
            value={value}
            onChange={e => handleChangeRecurso(record.id, "porcentagemUtilizacao", e.target.value)}
            style={{ borderRadius: 10, textAlign: "center" }}
          />
          <span style={{ fontSize: 16 }}>%</span>
        </Flex>
      ),
    },
    {
      title: "Qtd. Produção",
      dataIndex: "quantidadeProducao",
      key: "quantidadeProducao",
      fixed: "left",
      width: 180,
      align: "center",
      render: (value: number, record: any) => (
        <Input
          type="number"
          size="large"
          value={value}
          onChange={e => handleChangeRecurso(record.id, "quantidadeProducao", e.target.value)}
          placeholder="Qtd. Produção"
          style={{ borderRadius: 10, textAlign: "center" }}
        />
      ),
    },
    {
      title: "Alvo",
      dataIndex: "alvoIdsExternos",
      key: "alvoIdsExternos",
      fixed: "left",
      width: 200,
      align: "center",
      render: (value: string, record: any) => (
        <AlvoSelect
          id={value}
          ocupacaoId={ocupacaoId}
          onChange={(idExterno) => handleChangeRecurso(record.id, "alvoIdsExternos", idExterno)}
        />
      ),
    },
    {
      title: "Ações",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 120,
      align: "center",
      render: (_value: number, record: any, index: number) => {
        const hasAlerta = alertaByRecursoId[record.id];
        return (
          <Flex justify="center" align="center" gridGap={1}>
            <MdDelete
              size={18}
              color="#f00"
              style={{ cursor: "pointer" }}
              onClick={() => handleDeleteRecurso(index)}
            />
            {hasAlerta ? (
              <Tooltip title="Previsão de Falta de Insumo no Estoque">
                <span style={{ display: "inline-flex", cursor: "default" }}>
                  <MdWarning size={20} color="#e53935" />
                </span>
              </Tooltip>
            ) : (
              <span style={{ width: 28, height: 28, visibility: "hidden" }} aria-hidden />
            )}
          </Flex>
        );
      },
    },
  ];
}

export { getColumnsLocais, getColumnsRecursos }
